import { useState, useContext } from "react";
import { LangContext } from "../../LangContext";
import NewsCard from "../NewsCard";
import Meta from "../common/Meta";
import Spinner from "../common/Spinner";
import { pickKoreanTitle, cleanText } from "../../lib/utils";

const TIER_LIMIT = 5;

function sortByImportance(articles) {
  return [...articles].sort((a, b) => {
    const aCog = a.cog && a.cog !== "none" ? 1 : 0;
    const bCog = b.cog && b.cog !== "none" ? 1 : 0;
    if (bCog !== aCog) return bCog - aCog;
    return new Date(b.publishedAt) - new Date(a.publishedAt);
  });
}

function TierSection({ articles, onSelectArticle, onOverride, showDivider = true }) {
  const [visibleCount, setVisibleCount] = useState(TIER_LIMIT);
  const lang = useContext(LangContext);
  if (!articles || articles.length === 0) return null;

  const sorted = sortByImportance(articles);
  const visible = sorted.slice(0, visibleCount);
  const remaining = sorted.length - visibleCount;

  return (
    <div>
      {showDivider && (
        <div className="px-4 mt-1">
          <div className="border-t-2 border-white/60" />
        </div>
      )}
      {visible.map(n => (
        <NewsCard key={n.id} article={n} onClick={onSelectArticle} onOverride={onOverride} />
      ))}
      {remaining > 0 && (
        <div className="px-4 pt-1 pb-4 text-center">
          <button
            onClick={() => setVisibleCount(v => v + TIER_LIMIT)}
            className="bg-transparent border-none text-text-secondary text-[13px] font-semibold cursor-pointer py-1 w-full"
          >
            {lang === "en" ? "More" : "더 보기"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function NewsTab({ news, loading, error, byTier, themes, onLoad, onSelectArticle, onOverride }) {
  const lang = useContext(LangContext);
  const featured = byTier.gun[0];
  const restGun = byTier.gun.slice(1);

  return (
    <div>
      {loading && news.length === 0 && <Spinner message={lang === "en" ? "Loading news…" : "뉴스 수집 중…"} />}

      {error && (
        <div className="p-4 border-b border-border-line">
          <div className="text-sub text-tier-gun mb-1.5">{lang === "en" ? "Load failed" : "수집 실패"}</div>
          <div className="font-sans tabular text-[8px] text-text-tertiary bg-bg-secondary p-2 break-all whitespace-pre-wrap mb-2.5">
            {error}
          </div>
          <button
            onClick={onLoad}
            className="bg-transparent border border-border-line text-text-secondary text-[10px] py-1.5 px-3.5 cursor-pointer min-h-[44px]"
          >
            {lang === "en" ? "Retry" : "재시도"}
          </button>
        </div>
      )}

      {/* Theme widget */}
      {themes?.length > 0 && (
        <div className="p-4 border-b border-border-line bg-bg-secondary">
          <Meta className="block mb-2">{lang === "en" ? "Trending Themes" : "타매체 집중 테마"}</Meta>
          <div className="flex flex-col gap-2.5">
            {themes.map((th, ti) => (
              <div key={ti}>
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-sub font-bold text-text-primary tracking-wide">{th.label}</span>
                </div>
                {th.articles?.slice(0, 3).map((a, i) => (
                  <div
                    key={i}
                    onClick={() => a.url ? window.open(a.url, "_blank", "noopener,noreferrer") : null}
                    className="text-sub text-text-secondary leading-snug pl-2 border-l-[1.5px] border-border-line mb-0.5 cursor-pointer"
                  >
                    · {cleanText(pickKoreanTitle(a.hed_ko, a.hed))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Featured Smoking Gun */}
      {featured && (
        <NewsCard article={featured} onClick={onSelectArticle} onOverride={onOverride} />
      )}

      {restGun.map(n => (
        <NewsCard key={n.id} article={n} onClick={onSelectArticle} onOverride={onOverride} />
      ))}

      <TierSection
        articles={byTier.watershed}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
        showDivider={byTier.gun.length > 0}
      />
      <TierSection
        articles={byTier.notable}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
      />
      <TierSection
        articles={byTier.common}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
      />

      {!loading && news.length > 0 && (
        <div className="py-5 px-4 text-center">
          <button
            onClick={onLoad}
            className="bg-transparent border-none text-text-tertiary text-[16px] cursor-pointer py-2 w-full"
          >
            {lang === "en" ? "Refresh" : "새로고침"}
          </button>
        </div>
      )}
    </div>
  );
}
